import type { ComponentChildren, JSX } from "preact";
import { cn } from "./cn";

type Variant = "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

export type IconButtonProps = Omit<
  JSX.IntrinsicElements["button"],
  "size" | "class"
> & {
  ariaLabel: string;
  variant?: Variant;
  size?: Size;
  children: ComponentChildren;
  class?: string;
};

const VARIANT: Record<Variant, string> = {
  secondary:
    "bg-bg text-fg-muted border border-border hover:border-border-strong hover:bg-bg-subtle shadow-xs",
  ghost: "bg-transparent text-fg-subtle hover:bg-bg-subtle hover:text-fg",
  danger:
    "bg-bg text-danger-fg border border-border hover:border-danger-border hover:bg-danger-bg",
};

const SIZE: Record<Size, string> = {
  sm: "h-7 w-7 rounded-md",
  md: "h-8 w-8 rounded-md",
};

export function IconButton({
  ariaLabel,
  variant = "ghost",
  size = "md",
  children,
  class: extraClass,
  // Same rationale as Button: never let an icon button become an implicit
  // form submitter (HTML defaults <button> to type="submit").
  type = "button",
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      aria-label={ariaLabel}
      class={cn(
        "inline-flex items-center justify-center transition outline-none " +
          "focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-1 focus-visible:ring-offset-bg " +
          "disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANT[variant],
        SIZE[size],
        extraClass,
      )}
    >
      {children}
    </button>
  );
}
